import { Entity } from '@core/api/entity';
import { Field, Model, Serializer, Type } from '@junte/serialize-ts';

import { NumberSerializer } from './serializers';

/**
 * Pass-through serializer for JSON objects (identity function)
 * Used for matrix fields that come as JSON from the backend
 */
export class JsonObjectSerializer implements Serializer<any> {
  serialize(model: any): any {
    return model;
  }

  deserialize(dto: any): any {
    return dto;
  }
}

/**
 * Interface for portfolio performance data points (for chart visualization)
 */
export interface PortfolioPerformanceDataPoint {
  date: string; // ISO date string
  value: number; // Portfolio value
  returns?: number; // Optional period returns
}

/**
 * Chart data series for asset allocation over time
 */
export interface ChartSeries {
  name: string;
  values: number[];
}

/**
 * Chart data structure from backend
 */
export interface ChartData {
  label: string;
  x: number[];
  y?: number[]; // For chart1 (Portfolio Value)
  series?: ChartSeries[]; // For chart2 (Asset Allocation)
  currency?: string; // For chart1
  unit?: string; // For chart2
}

/**
 * Graph request body for POST /graphs endpoint
 * Backend expects snake_case for portfolios array items
 */
export interface PortfolioGraphRequestPortfolio {
  portfolio_uid: string;
  stocks_percentage: number;
  bonds_percentage: number;
  private_markets_percentage: number;
}

export interface PortfolioGraphRequest {
  capital_amount?: number;
  capitalAmount?: number; // Backend also accepts camelCase
  start_year?: number;
  startYear?: number; // Backend also accepts camelCase
  duration_years?: number;
  durationYears?: number; // Backend also accepts camelCase
  portfolios?: PortfolioGraphRequestPortfolio[]; // Array of portfolios for batch update
  // Legacy single portfolio format (deprecated, use portfolios array instead)
  model_name?: string;
  modelName?: string;
  stocks_percentage?: number;
  stocksPercentage?: number;
  bonds_percentage?: number;
  bondsPercentage?: number;
  private_markets_percentage?: number;
  privateMarketsPercentage?: number;
}

/**
 * Graph response from backend (returns camelCase)
 * Used for POST /graphs endpoint
 */
export interface PortfolioGraphResponse {
  inputs: {
    capitalAmount?: number;
    startYear?: number;
    durationYears?: number;
    modelName?: string;
    stocksPercentage?: number;
    bondsPercentage?: number;
    privateMarketsPercentage?: number;
  };
  charts: {
    chart1: ChartData;
    chart2: ChartData;
  };
  note?: string;
}

/**
 * Response from GET /graphs endpoint (initial load)
 * Returns all portfolios with their default allocations and chart data
 */
export interface PortfolioGraphsResponse {
  portfolios: Array<{
    uid: string;
    modelName: string;
    defaultAllocation: {
      stocksPercentage: number;
      bondsPercentage: number;
      privateMarketsPercentage: number;
    };
    chart1: ChartData;
  }>;
  globalParameters: {
    capitalAmount: number;
    startYear: number;
    durationYears: number;
  };
}

/**
 * Response from POST /graphs endpoint (when allocations change)
 * Returns only chart data for requested portfolios
 */
export interface PortfolioGraphsPostResponse {
  portfolios: Array<{
    uid: string;
    chart1: ChartData;
  }>;
}

/**
 * Portfolio Simulation Parameters Model
 * Stores simulation parameters (partner-scoped or global)
 */
@Model()
export class PortfolioSimulationParameters extends Entity {
  // Returns (as percentages) - API returns camelCase
  @Field()
  @Type(new NumberSerializer())
  public stocksReturn: number;

  @Field()
  @Type(new NumberSerializer())
  public bondsReturn: number;

  @Field()
  @Type(new NumberSerializer())
  public pmReturn: number; // private markets return

  @Field()
  @Type(new NumberSerializer())
  public rfRate: number; // risk-free rate

  // Volatility (as percentages)
  @Field()
  @Type(new NumberSerializer())
  public stocksVolatility: number;

  @Field()
  @Type(new NumberSerializer())
  public bondsVolatility: number;

  @Field()
  @Type(new NumberSerializer())
  public pmVolatility: number; // private markets volatility

  // Constraints (as percentages)
  @Field()
  @Type(new NumberSerializer())
  public minStocks: number;

  @Field()
  @Type(new NumberSerializer())
  public maxStocks: number;

  @Field()
  @Type(new NumberSerializer())
  public minBonds: number;

  @Field()
  @Type(new NumberSerializer())
  public maxBonds: number;

  @Field()
  @Type(new NumberSerializer())
  public minPm: number; // minimum private markets

  @Field()
  @Type(new NumberSerializer())
  public maxPm: number; // maximum private markets

  @Field()
  @Type(new NumberSerializer())
  public minDuration: number;

  @Field()
  @Type(new NumberSerializer())
  public maxDuration: number;

  @Field()
  @Type(new NumberSerializer())
  public defaultDuration: number;

  @Field()
  @Type(new NumberSerializer())
  public defaultCapital: number;

  // Correlations
  @Field()
  @Type(new NumberSerializer())
  public correlationStocksBonds: number;

  @Field()
  @Type(new NumberSerializer())
  public correlationStocksPm: number;

  @Field()
  @Type(new NumberSerializer())
  public correlationBondsPm: number;

  public static create(data: Partial<PortfolioSimulationParameters>): PortfolioSimulationParameters {
    return Object.assign(new PortfolioSimulationParameters(), data);
  }
}

/**
 * Portfolio Model
 * Represents a portfolio with allocation percentages and calculated metrics
 */
@Model()
export class PortfolioSimulationPortfolio extends Entity {
  // id field is inherited from Entity with @Name('uid') mapping

  // Basic fields - API returns camelCase
  @Field()
  public modelName: string; // max 30 chars

  @Field()
  @Type(new NumberSerializer())
  public stocksPercentage: number; // percentage (0-100)

  @Field()
  @Type(new NumberSerializer())
  public bondsPercentage: number; // percentage (0-100)

  @Field()
  @Type(new NumberSerializer())
  public privateMarketsPercentage: number; // percentage (0-100)

  @Field()
  public isDefault: boolean;

  // Read-only derived fields (calculated by backend) - API returns camelCase
  @Field()
  @Type(new NumberSerializer())
  public expectedReturn?: number;

  @Field()
  @Type(new NumberSerializer())
  public expectedVolatility?: number;

  @Field()
  @Type(new NumberSerializer())
  public sharpeRatio?: number;

  @Field()
  @Type(new NumberSerializer())
  public rfRate?: number; // risk-free rate

  @Field()
  @Type(new NumberSerializer())
  public endValue?: number;

  // Matrices (read-only, JSON objects) - API returns camelCase
  @Field()
  @Type(new JsonObjectSerializer())
  public volatilityMatrix?: Record<string, Record<string, number>>;

  @Field()
  @Type(new JsonObjectSerializer())
  public covarianceMatrix?: Record<string, Record<string, number>>;

  @Field()
  @Type(new JsonObjectSerializer())
  public correlationMatrix?: Record<string, Record<string, number>>;

  public static create(data: Partial<PortfolioSimulationPortfolio>): PortfolioSimulationPortfolio {
    return Object.assign(new PortfolioSimulationPortfolio(), data);
  }
}
